import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Headphones, ChevronRight, Check, X, Eye, EyeOff, Play, Pause, Volume2, Loader2, Crown } from 'lucide-react';
import ExerciseList from '@/components/ExerciseList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListeningPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [loading, setLoading] = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioData, setAudioData] = useState(null); // Can be single audio or segments
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [usageInfo, setUsageInfo] = useState({ used_today: 0, limit: 1 });
  const [isLocked, setIsLocked] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [playingQuestionId, setPlayingQuestionId] = useState(null);
  const audioRef = useRef(null);
  const questionAudioRef = useRef(null);

  useEffect(() => {
    fetchExercises();
    checkListeningUsage();
  }, [user?.level]);

  const checkListeningUsage = async () => {
    try {
      const response = await axios.get(`${API}/listening/usage`);
      setIsPremium(response.data.is_premium);
      setUsageInfo(response.data);
      if (!response.data.is_premium && response.data.used_today >= response.data.limit) {
        setIsLocked(true);
      }
    } catch (error) {
      console.error('Error checking listening usage:', error);
    }
  };

  const trackUsage = async () => {
    if (isPremium) return true;
    
    try {
      await axios.post(`${API}/listening/track-usage`);
      setUsageInfo(prev => ({ ...prev, used_today: prev.used_today + 1 }));
      return true;
    } catch (error) {
      if (error.response?.status === 403) {
        setIsLocked(true);
        return false;
      }
      return true;
    }
  };

  const fetchExercises = async () => {
    try {
      const params = new URLSearchParams();
      params.append('exercise_type', 'listening');
      if (user?.level) params.append('level', user.level);
      
      const response = await axios.get(`${API}/exercises?${params}`);
      setExercises(response.data);
      if (response.data.length > 0) {
        // Find first available exercise that is NOT instructions
        const firstAvailable = response.data.find(e => !e.is_locked && !e.is_instructions) || 
                              response.data.find(e => !e.is_locked) || 
                              response.data[0];
        setCurrentExercise(firstAvailable);
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectExercise = (exercise) => {
    if (exercise.is_locked) {
      toast.error('Este ejercicio requiere Premium');
      return;
    }
    setCurrentExercise(exercise);
    setAnswers({});
    setSubmitted(false);
    setResult(null);
    setShowTranscript(false);
    setAudioData(null);
    setIsPlaying(false);
    setCurrentSegment(0);
  };

  const generateAudio = async () => {
    // If the exercise has a pre-recorded audio URL, use it directly
    if (currentExercise?.content?.audio_url) {
      setAudioData({ url: currentExercise.content.audio_url, isUrl: true });
      toast.success('¡Audio listo para reproducir!');
      return;
    }
    
    if (!currentExercise?.content?.transcript) {
      toast.error('No hay transcripción disponible');
      return;
    }

    setAudioLoading(true);
    try {
      const response = await axios.post(`${API}/ai/text-to-speech`, {
        text: currentExercise.content.transcript,
        voice: 'onyx'
      });
      
      setAudioData(response.data);
      setCurrentSegment(0);
      
      if (response.data.multi_voice) {
        toast.success(`¡Audio generado con ${response.data.speakers?.length || 'múltiples'} voces!`);
      } else {
        toast.success('¡Audio generado! Haz clic en reproducir.');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setIsLocked(true);
        toast.error('Límite diario alcanzado. Actualiza a Premium.');
      } else {
        toast.error('Error al generar audio');
      }
    } finally {
      setAudioLoading(false);
    }
  };

  const getCurrentAudioSrc = () => {
    if (!audioData) return null;
    
    // If it's a URL-based audio
    if (audioData.isUrl) {
      return audioData.url;
    }
    
    if (audioData.multi_voice && audioData.audio_segments) {
      return `data:audio/mp3;base64,${audioData.audio_segments[currentSegment]?.audio_base64}`;
    }
    return `data:audio/mp3;base64,${audioData.audio_base64}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioEnd = () => {
    // If multi-voice, play next segment
    if (audioData?.multi_voice && audioData.audio_segments) {
      if (currentSegment < audioData.audio_segments.length - 1) {
        setCurrentSegment(prev => prev + 1);
        // Auto-play next segment after small delay
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.load();
            audioRef.current.play();
          }
        }, 300);
        return;
      }
    }
    setIsPlaying(false);
    setCurrentSegment(0);
  };

  const handleAnswerChange = (questionId, value) => {
    // Check if value is a number string (for multiple choice) or text (for sentence completion)
    const parsedValue = /^\d+$/.test(value) ? parseInt(value) : value;
    setAnswers(prev => ({
      ...prev,
      [questionId]: parsedValue
    }));
  };

  // Play question-specific audio
  const playQuestionAudio = (questionId, audioUrl) => {
    if (playingQuestionId === questionId && questionAudioRef.current && !questionAudioRef.current.paused) {
      // Pause if already playing this question
      questionAudioRef.current.pause();
      setPlayingQuestionId(null);
    } else {
      // Stop any currently playing audio
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      
      // Create new audio and play
      questionAudioRef.current = new Audio(audioUrl);
      questionAudioRef.current.onended = () => setPlayingQuestionId(null);
      questionAudioRef.current.onerror = () => {
        toast.error('Error al reproducir el audio');
        setPlayingQuestionId(null);
      };
      questionAudioRef.current.play();
      setPlayingQuestionId(questionId);
    }
  };

  const handleSubmit = async () => {
    if (!currentExercise) return;

    // Get the total number of questions based on exercise type
    const questions = currentExercise.content?.questions || [];
    const speakers = currentExercise.content?.speakers || [];
    const task1Speakers = currentExercise.content?.task1?.speakers || [];
    const task2Speakers = currentExercise.content?.task2?.speakers || [];
    const totalQuestions = task1Speakers.length + task2Speakers.length > 0 
      ? task1Speakers.length + task2Speakers.length 
      : speakers.length > 0 ? speakers.length : questions.length;
    
    if (Object.keys(answers).length < totalQuestions) {
      toast.error('Por favor, responde todas las preguntas');
      return;
    }

    // Track usage for free users (only on first submit of this exercise)
    if (!submitted && !isPremium) {
      const allowed = await trackUsage();
      if (!allowed) {
        toast.error('Has alcanzado el límite diario. Actualiza a Premium para continuar.');
        return;
      }
    }

    try {
      const response = await axios.post(`${API}/exercises/submit`, {
        exercise_id: currentExercise.id,
        exercise_type: 'listening',
        answers
      });
      
      setResult(response.data);
      setSubmitted(true);
      toast.success(`¡Completado! Puntuación: ${response.data.score}%`);
    } catch (error) {
      toast.error('Error al enviar las respuestas');
    }
  };

  const handleNextExercise = () => {
    const currentIndex = exercises.findIndex(e => e.id === currentExercise?.id);
    if (currentIndex < exercises.length - 1) {
      setCurrentExercise(exercises[currentIndex + 1]);
      setAnswers({});
      setSubmitted(false);
      setResult(null);
      setShowTranscript(false);
      setAudioData(null);
      setIsPlaying(false);
      setCurrentSegment(0);
    }
  };

  const handleReset = () => {
    setAnswers({});
    setSubmitted(false);
    setResult(null);
    setShowTranscript(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Cargando ejercicios...</div>
      </div>
    );
  }

  const questions = currentExercise?.content?.questions || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hidden audio element */}
      {audioData && (
        <audio
          ref={audioRef}
          src={getCurrentAudioSrc()}
          onEnded={handleAudioEnd}
        />
      )}

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
            <div className="flex items-center gap-2">
              <Headphones className="w-5 h-5 text-purple-500" />
              <span className="font-medium text-slate-900">Listening Practice</span>
            </div>
            {user?.level && (
              <span className={`level-badge level-${user.level.toLowerCase()}`}>
                {user.level}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Exercise List */}
        <ExerciseList
          exercises={exercises}
          currentExercise={currentExercise}
          onSelectExercise={handleSelectExercise}
          completedIds={[]}
          writtenInstructions={user?.level === 'B1' ? [
            "Answer all the questions.",
            "You can change your answers at any time during the test.",
            "",
            "There are four parts to this test.",
            "Each question carries one mark.",
            "You will hear each recording twice.",
            "For each part of the test there will be time for you to look through the questions and time for you to check your answers."
          ] : null}
        />

        {/* Usage Limit Banner */}
        {!isPremium && !isLocked && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-between animate-fade-in-up">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Plan Gratuito: {usageInfo.limit - usageInfo.used_today} ejercicio de listening restante hoy
                </p>
                <p className="text-xs text-amber-600">Premium = Listening ilimitado</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/subscription')}
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-white rounded-full"
            >
              Mejorar
            </Button>
          </div>
        )}

        {/* Locked Screen for Free Users */}
        {isLocked && (
          <div className="text-center py-16 animate-fade-in-up">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-slate-900 mb-2">
              Límite diario alcanzado
            </h2>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Has completado tu ejercicio de Listening gratuito de hoy. 
              Actualiza a Premium para acceso ilimitado a todos los ejercicios.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/subscription')}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-full px-8 py-6 text-lg font-medium"
              >
                <Crown className="w-5 h-5 mr-2" />
                Obtener Premium
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="rounded-full px-8 py-6"
              >
                Volver al Dashboard
              </Button>
            </div>
            <p className="text-sm text-slate-500 mt-6">
              El límite se reinicia mañana a medianoche
            </p>
          </div>
        )}

        {!isLocked && !currentExercise ? (
          <div className="text-center py-12">
            <Headphones className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              No hay ejercicios disponibles
            </h2>
            <p className="text-slate-600 mb-4">
              {user?.level 
                ? `No encontramos ejercicios de listening para el nivel ${user.level}`
                : 'Selecciona un nivel para ver los ejercicios'}
            </p>
            <Button onClick={() => navigate('/level-select')} className="rounded-full">
              Seleccionar Nivel
            </Button>
          </div>
        ) : !isLocked && (
          <div className="space-y-6">
            {/* Title + Audio Section - Full Width */}
            <div className="animate-fade-in-up">
              <div className="bg-white border border-slate-200 p-6">
                {/* Exercise Info */}
                <div className="mb-6">
                  <span className={`level-badge level-${currentExercise.level.toLowerCase()} mb-3`}>
                    {currentExercise.level}
                  </span>
                  <h1 className="font-['Playfair_Display'] text-2xl font-bold text-slate-900 mb-1">
                    {currentExercise.title}
                  </h1>
                  <p className="text-sm text-orange-600">{currentExercise.title_es}</p>
                </div>

                {/* Audio Player - Full Width */}
                <div className="p-6 bg-slate-900 rounded-sm text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Headphones className="w-10 h-10 text-purple-400" />
                      <div>
                        <p className="font-medium">Audio de Práctica</p>
                        <p className="text-sm text-slate-400">
                          {audioData ? 'Audio listo para reproducir' : 'Genera el audio para escuchar'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {!audioData ? (
                        <Button
                          onClick={generateAudio}
                          disabled={audioLoading}
                          className="bg-purple-600 hover:bg-purple-500 rounded-full"
                          data-testid="generate-audio-btn"
                        >
                          {audioLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generando...
                            </>
                          ) : (
                            <>
                              <Volume2 className="w-4 h-4 mr-2" />
                              Generar Audio
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={togglePlay}
                          className={`rounded-full w-14 h-14 ${isPlaying ? 'bg-orange-500 hover:bg-orange-400' : 'bg-purple-600 hover:bg-purple-500'}`}
                          data-testid="play-audio-btn"
                        >
                          {isPlaying ? (
                            <Pause className="w-6 h-6" />
                          ) : (
                            <Play className="w-6 h-6 ml-1" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Show current speaker for multi-voice */}
                  {audioData?.multi_voice && audioData.audio_segments && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <p className="text-sm text-slate-400">
                        Locutor: <span className="text-white font-medium">{audioData.audio_segments[currentSegment]?.speaker}</span>
                        <span className="text-slate-500 ml-2">
                          ({currentSegment + 1}/{audioData.audio_segments.length})
                        </span>
                      </p>
                    </div>
                  )}
                  
                  {!isPremium && !audioData && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <button
                        onClick={() => navigate('/subscription')}
                        className="flex items-center gap-2 text-amber-400 text-sm hover:text-amber-300"
                      >
                        <Crown className="w-4 h-4" />
                        Premium: Audio ilimitado de alta calidad
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Questions Section - Full Width */}
            <div className="animate-fade-in-up animation-delay-200">
              <div className="bg-white border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-1">Questions</h2>
                <p className="text-sm text-slate-500 mb-4">Responde basándote en lo que escuches</p>

                {/* Part description / instructions - shown for all parts */}
                {currentExercise?.content?.part_description && (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded mb-6">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{currentExercise.content.part_description}</pre>
                  </div>
                )}

                {/* C1 Part 4: Dual Task Matching Format */}
                {currentExercise?.content?.task_type === 'dual_task_matching' && currentExercise?.content?.task1 && currentExercise?.content?.task2 ? (
                  <div className="space-y-4">
                    {/* Block 2: Dual task grid - side by side */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Task 1 */}
                      <div className="bg-slate-50 border border-slate-200 rounded p-3">
                        <h3 className="text-sm font-bold text-slate-900 mb-2">{currentExercise.content.task1.title}</h3>
                        <div className="flex gap-2">
                          <div className="space-y-1.5 shrink-0">
                            {currentExercise.content.task1.speakers.map((speaker) => (
                              <div key={speaker.id} className="flex items-center gap-1 h-[26px]">
                                <span className="text-sm font-semibold text-slate-600 whitespace-nowrap">{speaker.question_number}</span>
                                <select
                                  value={answers[speaker.id] || ''}
                                  onChange={(e) => handleAnswerChange(speaker.id, e.target.value)}
                                  disabled={submitted}
                                  data-testid={`dropdown-${speaker.id}`}
                                  className={`w-14 text-sm border rounded px-1 py-0.5 ${
                                    submitted && answers[speaker.id] === speaker.correct_answer ? 'bg-green-100 border-green-400' :
                                    submitted && answers[speaker.id] && answers[speaker.id] !== speaker.correct_answer ? 'bg-red-100 border-red-400' :
                                    'border-slate-300'
                                  }`}
                                >
                                  <option value="">-</option>
                                  {currentExercise.content.task1.options.map((opt) => (
                                    <option key={opt.letter} value={opt.letter}>{opt.letter}</option>
                                  ))}
                                </select>
                                {submitted && answers[speaker.id] !== speaker.correct_answer && (
                                  <span className="text-sm text-green-600 font-medium">{speaker.correct_answer}</span>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="space-y-1.5 border-l border-slate-200 pl-2 flex-1 min-w-0">
                            {currentExercise.content.task1.options.map((opt) => (
                              <p key={opt.letter} className="text-sm text-slate-700 leading-snug">
                                <span className="font-bold">{opt.letter}</span> {opt.text}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Task 2 */}
                      <div className="bg-slate-50 border border-slate-200 rounded p-3">
                        <h3 className="text-sm font-bold text-slate-900 mb-2">{currentExercise.content.task2.title}</h3>
                        <div className="flex gap-2">
                          <div className="space-y-1.5 shrink-0">
                            {currentExercise.content.task2.speakers.map((speaker) => (
                              <div key={speaker.id} className="flex items-center gap-1 h-[26px]">
                                <span className="text-sm font-semibold text-slate-600 whitespace-nowrap">{speaker.question_number}</span>
                                <select
                                  value={answers[speaker.id] || ''}
                                  onChange={(e) => handleAnswerChange(speaker.id, e.target.value)}
                                  disabled={submitted}
                                  data-testid={`dropdown-${speaker.id}`}
                                  className={`w-14 text-sm border rounded px-1 py-0.5 ${
                                    submitted && answers[speaker.id] === speaker.correct_answer ? 'bg-green-100 border-green-400' :
                                    submitted && answers[speaker.id] && answers[speaker.id] !== speaker.correct_answer ? 'bg-red-100 border-red-400' :
                                    'border-slate-300'
                                  }`}
                                >
                                  <option value="">-</option>
                                  {currentExercise.content.task2.options.map((opt) => (
                                    <option key={opt.letter} value={opt.letter}>{opt.letter}</option>
                                  ))}
                                </select>
                                {submitted && answers[speaker.id] !== speaker.correct_answer && (
                                  <span className="text-sm text-green-600 font-medium">{speaker.correct_answer}</span>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="space-y-1.5 border-l border-slate-200 pl-2 flex-1 min-w-0">
                            {currentExercise.content.task2.options.map((opt) => (
                              <p key={opt.letter} className="text-sm text-slate-700 leading-snug">
                                <span className="font-bold">{opt.letter}</span> {opt.text}
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : currentExercise?.content?.task_type === 'multiple_matching' && currentExercise?.content?.options && currentExercise?.content?.speakers ? (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Options in 2 columns (A B, C D, E F, G H) - 2/3 width */}
                    <div className="lg:col-span-2 bg-slate-50 border border-slate-200 p-4 rounded">
                      <h3 className="text-sm font-medium text-slate-600 mb-4">Options / Opciones</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {currentExercise.content.options.map((option) => (
                          <div key={option.letter} className="flex items-start gap-2 p-2 rounded bg-white border border-slate-200">
                            <span className="font-bold text-orange-600 min-w-5">{option.letter}</span>
                            <div>
                              <p className="text-sm text-slate-800">{option.text}</p>
                              <p className="text-xs text-slate-500">{option.text_es}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Right: Speaker dropdowns - 1/3 width */}
                    <div className="lg:col-span-1 bg-white border border-slate-200 p-4 rounded">
                      <h3 className="text-sm font-medium text-slate-600 mb-4">Match each speaker / Empareja cada hablante</h3>
                      <div className="space-y-3">
                        {currentExercise.content.speakers.map((speaker) => {
                          const isCorrect = submitted && answers[speaker.id] === speaker.correct_answer;
                          const isWrong = submitted && answers[speaker.id] && answers[speaker.id] !== speaker.correct_answer;
                          
                          return (
                            <div key={speaker.id} className={`flex items-center justify-between p-3 rounded border ${
                              isCorrect ? 'bg-green-50 border-green-300' :
                              isWrong ? 'bg-red-50 border-red-300' :
                              'bg-slate-50 border-slate-200'
                            }`}>
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-slate-700">{speaker.label}</span>
                                <span className="text-xs text-slate-400">{speaker.question_number}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <select
                                  value={answers[speaker.id] || ''}
                                  onChange={(e) => handleAnswerChange(speaker.id, e.target.value)}
                                  disabled={submitted}
                                  className={`px-3 py-2 border rounded text-sm font-medium min-w-[60px] ${
                                    isCorrect ? 'border-green-500 bg-green-100' :
                                    isWrong ? 'border-red-500 bg-red-100' :
                                    'border-slate-300 bg-white'
                                  }`}
                                >
                                  <option value="">-</option>
                                  {currentExercise.content.options.map((opt) => (
                                    <option key={opt.letter} value={opt.letter}>{opt.letter}</option>
                                  ))}
                                </select>
                                {isCorrect && <Check className="w-5 h-5 text-green-600" />}
                                {isWrong && (
                                  <span className="text-sm font-medium text-green-600">{speaker.correct_answer}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : currentExercise?.content?.task_type === 'sentence_completion' || questions.some(q => q.sentence) ? (
                  /* Part 2: Sentence Completion - 5 rows of 2 questions */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {questions.map((question, qIndex) => {
                      const isCorrect = submitted && question.correct_answer && 
                        (question.acceptable_answers || [question.correct_answer]).some(a => 
                          a.toLowerCase().trim() === (answers[question.id] || '').toLowerCase().trim()
                        );
                      const isWrong = submitted && question.correct_answer && !isCorrect;
                      
                      return (
                        <div key={question.id} className={`p-4 rounded border ${
                          isCorrect ? 'bg-green-50 border-green-300' :
                          isWrong ? 'bg-red-50 border-red-300' :
                          'bg-slate-50 border-slate-200'
                        }`}>
                          {/* Question Audio Button */}
                          {question.audio_url && (
                            <button
                              onClick={() => playQuestionAudio(question.id, question.audio_url)}
                              className={`mb-3 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                playingQuestionId === question.id
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                              }`}
                            >
                              {playingQuestionId === question.id ? (
                                <><Pause className="w-3 h-3" /> Pausar</>
                              ) : (
                                <><Play className="w-3 h-3" /> Audio</>
                              )}
                            </button>
                          )}
                          
                          <p className="font-medium text-slate-900 mb-1 text-sm">
                            {question.question_number || qIndex + 1}. {question.sentence}
                          </p>
                          {question.sentence_es && (
                            <p className="text-xs text-slate-500 mb-3">{question.sentence_es}</p>
                          )}
                          <Input
                            type="text"
                            placeholder="Write your answer..."
                            value={answers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            disabled={submitted}
                            className={`w-full h-9 text-sm ${
                              isCorrect ? 'border-green-500 bg-green-100' :
                              isWrong ? 'border-red-500 bg-red-100' : ''
                            }`}
                          />
                          {submitted && question.correct_answer && (
                            <p className="mt-2 text-xs">
                              <span className="font-medium text-green-600">✓ </span>
                              <span className="text-slate-700">{question.correct_answer}</span>
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : currentExercise?.content?.task_type === 'multiple_choice_visual' ? (
                  /* B1 Part 1: Multiple Choice with Images - full width, radio buttons overlaid on image panels */
                  <div className="space-y-6">
                    {questions.map((question, qIndex) => {
                      const isCorrect = submitted && answers[question.id] === question.correct;
                      const isWrong = submitted && answers[question.id] !== undefined && answers[question.id] !== question.correct;
                      return (
                        <div key={question.id} className={`p-4 rounded border ${
                          isCorrect ? 'bg-green-50 border-green-300' :
                          isWrong ? 'bg-red-50 border-red-300' :
                          'bg-slate-50 border-slate-200'
                        }`}>
                          <p className="font-medium text-slate-900 mb-3 text-sm">
                            {question.number || qIndex + 1}. {question.question}
                          </p>
                          {question.image_url && (
                            <RadioGroup
                              value={answers[question.id]?.toString()}
                              onValueChange={(value) => handleAnswerChange(question.id, parseInt(value))}
                              disabled={submitted}
                              className="relative w-full"
                            >
                              <div className="relative">
                                <img src={question.image_url} alt={`Question ${question.number || qIndex + 1}`} className="w-full rounded border border-slate-200" />
                                {/* Radio buttons positioned at bottom center of each third */}
                                <div className="absolute bottom-3 left-0 right-0 grid grid-cols-3">
                                  {['A', 'B', 'C'].map((label, oIndex) => {
                                    const optIsCorrect = submitted && oIndex === question.correct;
                                    const optIsSelected = answers[question.id] === oIndex;
                                    return (
                                      <div key={oIndex} className="flex justify-center">
                                        <label htmlFor={`${question.id}-${oIndex}`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer shadow-sm ${
                                          optIsCorrect ? 'bg-green-500 text-white' :
                                          submitted && optIsSelected && !optIsCorrect ? 'bg-red-500 text-white' :
                                          optIsSelected ? 'bg-orange-500 text-white' :
                                          'bg-white/90 text-slate-800 hover:bg-white border border-slate-300'
                                        }`}>
                                          <RadioGroupItem value={oIndex.toString()} id={`${question.id}-${oIndex}`} className="h-4 w-4" />
                                          <span className="text-sm font-bold">{label}</span>
                                          {optIsCorrect && <Check className="w-4 h-4" />}
                                          {submitted && optIsSelected && !optIsCorrect && <X className="w-4 h-4" />}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </RadioGroup>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : currentExercise?.title?.includes('Part 4') ? (
                  /* Part 4: Multiple Choice - Full width, one question per row */
                  <div className="space-y-4">
                    {questions.map((question, qIndex) => {
                      const isCorrect = submitted && answers[question.id] === question.correct;
                      const isWrong = submitted && answers[question.id] !== undefined && answers[question.id] !== question.correct;
                      
                      return (
                        <div key={question.id} className={`p-4 rounded border ${
                          isCorrect ? 'bg-green-50 border-green-300' :
                          isWrong ? 'bg-red-50 border-red-300' :
                          'bg-slate-50 border-slate-200'
                        }`}>
                          {/* Question Audio Button */}
                          {question.audio_url && (
                            <button
                              onClick={() => playQuestionAudio(question.id, question.audio_url)}
                              className={`mb-3 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                playingQuestionId === question.id
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                              }`}
                            >
                              {playingQuestionId === question.id ? (
                                <><Pause className="w-3 h-3" /> Pausar</>
                              ) : (
                                <><Play className="w-3 h-3" /> Audio Q{question.question_number || question.number || qIndex + 1}</>
                              )}
                            </button>
                          )}
                          
                          {question.context && (
                            <p className="text-sm text-slate-600 italic mb-3 bg-white p-2 rounded border border-slate-200">
                              {question.context}
                            </p>
                          )}
                          
                          <p className="font-medium text-slate-900 mb-1">
                            {question.question_number || question.number || qIndex + 1}. {question.question}
                          </p>
                          {question.question_es && (
                            <p className="text-sm text-slate-500 mb-3">{question.question_es}</p>
                          )}
                          
                          <RadioGroup
                            value={answers[question.id]?.toString()}
                            onValueChange={(value) => handleAnswerChange(question.id, value)}
                            disabled={submitted}
                            className="space-y-2"
                          >
                            {question.options?.map((option, oIndex) => {
                              const optIsCorrect = submitted && oIndex === question.correct;
                              const optIsSelected = answers[question.id] === oIndex;
                              const optionLabel = ['A', 'B', 'C', 'D'][oIndex] || (oIndex + 1);
                              
                              return (
                                <div key={oIndex} className={`flex items-center gap-3 p-3 rounded border ${
                                  optIsCorrect ? 'bg-green-100 border-green-300' :
                                  submitted && optIsSelected && !optIsCorrect ? 'bg-red-100 border-red-300' :
                                  'bg-white border-slate-200 hover:bg-slate-50'
                                }`}>
                                  <RadioGroupItem value={oIndex.toString()} id={`${question.id}-${oIndex}`} className="h-4 w-4" />
                                  <Label htmlFor={`${question.id}-${oIndex}`} className="cursor-pointer text-sm flex-1">
                                    <span className="font-semibold text-slate-600 mr-2">{optionLabel}.</span>
                                    {option}
                                  </Label>
                                  {optIsCorrect && <Check className="w-5 h-5 text-green-600" />}
                                  {submitted && optIsSelected && !optIsCorrect && <X className="w-5 h-5 text-red-600" />}
                                </div>
                              );
                            })}
                          </RadioGroup>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Part 1: Multiple Choice - Dynamic grid (2 rows of 3 for 6 questions, 2 rows of 4 for 8) */
                  <div className="space-y-4">
                    {/* Row 1 */}
                    <div className={`grid grid-cols-2 ${questions.length <= 6 ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-4`}>
                      {questions.slice(0, questions.length <= 6 ? 3 : 4).map((question, qIndex) => (
                        <div key={question.id} className="bg-slate-50 border border-slate-200 rounded p-3">
                          {/* Question Audio Button */}
                          {question.audio_url && (
                            <button
                              onClick={() => playQuestionAudio(question.id, question.audio_url)}
                              className={`mb-2 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                                playingQuestionId === question.id
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                              }`}
                            >
                              {playingQuestionId === question.id ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                            </button>
                          )}
                          
                          {question.context && (
                            <p className="text-xs text-slate-600 italic mb-1">{question.context}</p>
                          )}
                          {question.question && (
                            <p className="text-xs font-semibold text-slate-800 mb-2">{question.question}</p>
                          )}
                          
                          <RadioGroup
                            value={answers[question.id]?.toString()}
                            onValueChange={(value) => handleAnswerChange(question.id, value)}
                            disabled={submitted}
                            className="space-y-1"
                          >
                            {question.options?.map((option, oIndex) => {
                              const isCorrect = submitted && oIndex === question.correct;
                              const isSelected = answers[question.id] === oIndex;
                              const optionLabel = ['A', 'B', 'C', 'D'][oIndex] || (oIndex + 1);
                              
                              return (
                                <div key={oIndex} className={`flex items-center gap-2 p-1.5 rounded ${
                                  submitted && isCorrect ? 'bg-green-100' :
                                  submitted && isSelected && !isCorrect ? 'bg-red-100' :
                                  'hover:bg-white'
                                }`}>
                                  {oIndex === 0 && (
                                    <span className="font-bold text-slate-900 min-w-6">({question.question_number || question.number || qIndex + 1})</span>
                                  )}
                                  {oIndex !== 0 && <span className="min-w-6"></span>}
                                  <RadioGroupItem value={oIndex.toString()} id={`${question.id}-${oIndex}`} className="h-4 w-4" />
                                  <Label htmlFor={`${question.id}-${oIndex}`} className="cursor-pointer text-xs flex-1">
                                    {optionLabel}. {option}
                                  </Label>
                                  {submitted && isCorrect && <Check className="w-4 h-4 text-green-600" />}
                                </div>
                              );
                            })}
                          </RadioGroup>
                        </div>
                      ))}
                    </div>
                    
                    {/* Row 2 */}
                    {questions.length > (questions.length <= 6 ? 3 : 4) && (
                      <div className={`grid grid-cols-2 ${questions.length <= 6 ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-4`}>
                        {questions.slice(questions.length <= 6 ? 3 : 4, questions.length <= 6 ? 6 : 8).map((question, qIndex) => (
                          <div key={question.id} className="bg-slate-50 border border-slate-200 rounded p-3">
                            {/* Question Audio Button */}
                            {question.audio_url && (
                              <button
                                onClick={() => playQuestionAudio(question.id, question.audio_url)}
                                className={`mb-2 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                                  playingQuestionId === question.id
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                }`}
                              >
                                {playingQuestionId === question.id ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                              </button>
                            )}
                            
                            {question.context && (
                              <p className="text-xs text-slate-600 italic mb-1">{question.context}</p>
                            )}
                            {question.question && (
                              <p className="text-xs font-semibold text-slate-800 mb-2">{question.question}</p>
                            )}
                            
                            <RadioGroup
                              value={answers[question.id]?.toString()}
                              onValueChange={(value) => handleAnswerChange(question.id, value)}
                              disabled={submitted}
                              className="space-y-1"
                            >
                              {question.options?.map((option, oIndex) => {
                                const isCorrect = submitted && oIndex === question.correct;
                                const isSelected = answers[question.id] === oIndex;
                                const optionLabel = ['A', 'B', 'C', 'D'][oIndex] || (oIndex + 1);
                                
                                return (
                                  <div key={oIndex} className={`flex items-center gap-2 p-1.5 rounded ${
                                    submitted && isCorrect ? 'bg-green-100' :
                                    submitted && isSelected && !isCorrect ? 'bg-red-100' :
                                    'hover:bg-white'
                                  }`}>
                                    {oIndex === 0 && (
                                      <span className="font-bold text-slate-900 min-w-6">({question.question_number || question.number || qIndex + (questions.length <= 6 ? 4 : 5)})</span>
                                    )}
                                    {oIndex !== 0 && <span className="min-w-6"></span>}
                                    <RadioGroupItem value={oIndex.toString()} id={`${question.id}-${oIndex}`} className="h-4 w-4" />
                                    <Label htmlFor={`${question.id}-${oIndex}`} className="cursor-pointer text-xs flex-1">
                                      {optionLabel}. {option}
                                    </Label>
                                    {submitted && isCorrect && <Check className="w-4 h-4 text-green-600" />}
                                  </div>
                                );
                              })}
                            </RadioGroup>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Result */}
                {submitted && result && (
                  <div className={`mt-6 p-4 rounded-sm ${
                    result.score >= 70 ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'
                  }`}>
                    <p className="font-semibold text-lg">
                      Puntuación: {result.score}%
                    </p>
                    <p className="text-sm text-slate-600">
                      {result.score >= 70 
                        ? '¡Excelente comprensión auditiva!' 
                        : 'Practica escuchando más contenido en inglés.'}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 flex gap-3">
                  {!submitted ? (
                    <Button
                      onClick={handleSubmit}
                      className="flex-1 bg-slate-900 hover:bg-slate-800 rounded-full"
                      data-testid="submit-btn"
                    >
                      Enviar Respuestas
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={handleReset}
                        variant="outline"
                        className="flex-1 rounded-full"
                        data-testid="retry-btn"
                      >
                        Reintentar
                      </Button>
                      {exercises.findIndex(e => e.id === currentExercise?.id) < exercises.length - 1 && (
                        <Button
                          onClick={handleNextExercise}
                          className="flex-1 bg-orange-600 hover:bg-orange-500 rounded-full"
                          data-testid="next-btn"
                        >
                          Siguiente
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
